//src/pages/PainelCliente?TikpagExperienceEscolha.jsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";

import {
  ArrowLeft,
  CheckCircle2,
  Clock3,
  Gift,
  Medal,
  RefreshCcw,
  Sparkles
} from "lucide-react";

import {
  useNavigate,
  useParams
} from "react-router-dom";

import axios from "../../axiosConfig";
import "./TikpagExperienceEscolha.css";

const formatarDinheiro = (valor) => {
  if (
    valor === null ||
    valor === undefined ||
    valor === ""
  ) {
    return "Não informado";
  }

  return Number(valor).toLocaleString(
    "pt-BR",
    {
      style: "currency",
      currency: "BRL"
    }
  );
};

const formatarData = (valor) => {
  if (!valor) return "-";

  const texto = String(valor).slice(0, 10);
  const [ano, mes, dia] = texto.split("-");

  if (!ano || !mes || !dia) return valor;

  return `${dia}/${mes}/${ano}`;
};

export default function TikpagExperienceEscolha() {
  const navigate = useNavigate();
  const { conquistaId } = useParams();

  const [dados, setDados] = useState(null);
  const [experienciaSelecionada, setExperienciaSelecionada] =
    useState(null);

  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");

  const carregar = useCallback(async () => {
    const id = Number(conquistaId);

    if (
      !Number.isSafeInteger(id) ||
      id <= 0
    ) {
      setErro("Conquista inválida.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setErro("");

    try {
      const resposta = await axios.get(
        `/experience/conquistas/${id}/experiencias`
      );

      setDados(resposta.data || null);

      if (resposta.data?.escolha) {
        setExperienciaSelecionada(
          Number(
            resposta.data.escolha.experiencia_id
          )
        );
      }
    } catch (err) {
      console.error(
        "Erro ao carregar experiências da conquista:",
        err
      );

      setErro(
        err.response?.data?.error ||
          "Não foi possível carregar as experiências disponíveis."
      );
    } finally {
      setLoading(false);
    }
  }, [conquistaId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const conquista = dados?.conquista || null;
  const escolha = dados?.escolha || null;

  const experiencias = useMemo(
    () =>
      Array.isArray(dados?.experiencias)
        ? dados.experiencias
        : [],
    [dados]
  );

  const experienciaSelecionadaDetalhe =
    experiencias.find(
      (experiencia) =>
        Number(experiencia.id) ===
        Number(experienciaSelecionada)
    ) || null;

  const confirmarEscolha = async () => {
    if (!conquista?.pode_escolher) {
      setErro(
        "Esta conquista não está disponível para uma nova escolha."
      );
      return;
    }

    if (!experienciaSelecionadaDetalhe) {
      setErro(
        "Selecione uma experiência antes de continuar."
      );
      return;
    }

    const confirmou = window.confirm(
      `Confirma a experiência "${experienciaSelecionadaDetalhe.titulo}"?\n\n` +
        "Depois da confirmação, a escolha não poderá ser alterada pelo cliente."
    );

    if (!confirmou) return;

    setSalvando(true);
    setErro("");
    setSucesso("");

    try {
      const resposta = await axios.post(
        `/experience/conquistas/${conquista.id}/escolher`,
        {
          experiencia_id:
            Number(
              experienciaSelecionadaDetalhe.id
            )
        }
      );

      setSucesso(
        resposta.data?.message ||
          "Experiência escolhida com sucesso."
      );

      await carregar();
    } catch (err) {
      console.error(
        "Erro ao registrar escolha da experiência:",
        err
      );

      setErro(
        err.response?.data?.error ||
          "Não foi possível confirmar a experiência."
      );
    } finally {
      setSalvando(false);
    }
  };

  if (loading) {
    return (
      <div className="experienceEscolha-page">
        <div className="experienceEscolha-loading">
          <RefreshCcw
            size={28}
            className="experienceEscolha-spin"
          />

          <strong>
            Carregando experiências...
          </strong>
        </div>
      </div>
    );
  }

  return (
    <div className="experienceEscolha-page">
      <button
        type="button"
        className="experienceEscolha-backButton"
        onClick={() =>
          navigate(
            "/painel-cliente/tikpag-experience"
          )
        }
      >
        <ArrowLeft size={16} />
        Voltar para minha jornada
      </button>

      {erro && (
        <div className="experienceEscolha-alert erro">
          {erro}
        </div>
      )}

      {sucesso && (
        <div className="experienceEscolha-alert sucesso">
          <CheckCircle2 size={17} />
          {sucesso}
        </div>
      )}

      {conquista && (
        <section className="experienceEscolha-hero">
          <div className="experienceEscolha-pinVisual">
            {conquista.pin_imagem ? (
              <img
                src={conquista.pin_imagem}
                alt={conquista.pin_nome}
              />
            ) : (
              <Medal size={46} />
            )}
          </div>

          <div className="experienceEscolha-heroContent">
            <span className="experienceEscolha-kicker">
              Parabéns pela conquista
            </span>

            <h1>
              Você conquistou o PIN{" "}
              {conquista.pin_nome}
            </h1>

            <p>
              Escolha uma das experiências
              disponíveis para celebrar seu
              resultado nesta competência.
            </p>

            <div className="experienceEscolha-meta">
              <span>
                <Clock3 size={14} />
                {String(conquista.mes).padStart(
                  2,
                  "0"
                )}
                /{conquista.ano}
              </span>

              <span>
                <Sparkles size={14} />
                Volume apurado:{" "}
                {formatarDinheiro(
                  conquista.volume_apurado
                )}
              </span>
            </div>
          </div>
        </section>
      )}

      {escolha ? (
        <section className="experienceEscolha-confirmed">
          <div className="experienceEscolha-confirmedIcon">
            <CheckCircle2 size={29} />
          </div>

          <div>
            <span>
              Experiência confirmada
            </span>

            <h2>
              {escolha.experiencia_titulo}
            </h2>

            <p>
              Sua escolha foi registrada em{" "}
              {formatarData(
                escolha.escolhida_em
              )}
              . O status atual é{" "}
              <strong>{escolha.status}</strong>.
            </p>

            {escolha.validade && (
              <small>
                Validade:{" "}
                {formatarData(
                  escolha.validade
                )}
              </small>
            )}

            {escolha.codigo_voucher && (
              <small>
                Código do voucher:{" "}
                {escolha.codigo_voucher}
              </small>
            )}
          </div>

          {escolha.experiencia_imagem && (
            <img
              src={
                escolha.experiencia_imagem
              }
              alt={
                escolha.experiencia_titulo
              }
            />
          )}
        </section>
      ) : (
        <>
          <section className="experienceEscolha-title">
            <div>
              <span>
                Experiências disponíveis
              </span>

              <h2>
                Escolha uma opção
              </h2>

              <p>
                Confira os detalhes antes de
                confirmar. A escolha é única para
                esta conquista.
              </p>
            </div>

            <strong>
              {experiencias.length}{" "}
              {experiencias.length === 1
                ? "opção"
                : "opções"}
            </strong>
          </section>

          <section className="experienceEscolha-grid">
            {experiencias.map(
              (experiencia) => {
                const selecionada =
                  Number(
                    experienciaSelecionada
                  ) ===
                  Number(experiencia.id);

                return (
                  <button
                    type="button"
                    className={`experienceEscolha-card ${
                      selecionada
                        ? "selecionada"
                        : ""
                    }`}
                    key={experiencia.id}
                    onClick={() =>
                      setExperienciaSelecionada(
                        Number(
                          experiencia.id
                        )
                      )
                    }
                  >
                    <div className="experienceEscolha-cardImage">
                      {experiencia.imagem_url ? (
                        <img
                          src={
                            experiencia.imagem_url
                          }
                          alt={
                            experiencia.titulo
                          }
                        />
                      ) : (
                        <Gift size={34} />
                      )}

                      {selecionada && (
                        <span>
                          <CheckCircle2
                            size={17}
                          />
                        </span>
                      )}
                    </div>

                    <div className="experienceEscolha-cardBody">
                      <span className="experienceEscolha-category">
                        {experiencia.categoria_nome ||
                          "Experiência"}
                      </span>

                      <h3>
                        {experiencia.titulo}
                      </h3>

                      <p>
                        {experiencia.descricao ||
                          "Sem descrição cadastrada."}
                      </p>

                      <div className="experienceEscolha-cardMeta">
                        <span>
                          {experiencia.fornecedor ||
                            "Fornecedor a definir"}
                        </span>

                        <span>
                          {experiencia.validade_dias
                            ? `${experiencia.validade_dias} dias de validade`
                            : "Validade a definir"}
                        </span>
                      </div>

                      {experiencia.instrucoes && (
                        <small>
                          {experiencia.instrucoes}
                        </small>
                      )}
                    </div>
                  </button>
                );
              }
            )}

            {!experiencias.length && (
              <div className="experienceEscolha-empty">
                <Gift size={31} />

                <h3>
                  Nenhuma experiência disponível
                </h3>

                <p>
                  Não há opções ativas ou com
                  disponibilidade para este PIN
                  neste momento.
                </p>
              </div>
            )}
          </section>

          <section className="experienceEscolha-confirmBar">
            <div>
              <span>
                Sua seleção
              </span>

              <strong>
                {experienciaSelecionadaDetalhe
                  ?.titulo ||
                  "Nenhuma experiência selecionada"}
              </strong>
            </div>

            <button
              type="button"
              onClick={confirmarEscolha}
              disabled={
                salvando ||
                !experienciaSelecionadaDetalhe ||
                !conquista?.pode_escolher
              }
            >
              {salvando ? (
                <RefreshCcw
                  size={17}
                  className="experienceEscolha-spin"
                />
              ) : (
                <CheckCircle2 size={17} />
              )}

              Confirmar experiência
            </button>
          </section>
        </>
      )}
    </div>
  );
}